import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import usePageMeta from '../hooks/usePageMeta';
import { track, EVENTS } from '../services/analytics';
import { problem, odds } from '../data/evidence.js';
import { SiteFooter } from '../components/SiteHeader';
import EvidenceCharts from '../components/EvidenceCharts';
import BuildFilm from '../components/BuildFilm';

// Variant A, picked by the founder on 2026-09-16 from four mocks. The copy
// and structure are the shipped static page's, moved into the SPA unchanged;
// every number names its source and there is no invented social proof.
//
// Round 13 (2026-09-18, A): the hero asks for the one thing that works instead of arguing first. A founder with
// a landing page is two decisions from a deck in their own colours, so the field is the hero and the thesis
// moves down to head the evidence it already belonged to. The arc states what Fullrun now is, end to end, with
// the room fourth: rooms changed the product and the page described only the rehearsal.
// What each step costs the founder, not how long it takes: nothing here measures a duration, and an unsourced
// number does not ship (product invariant 4; reviewer, PR #63).
const ARC = [
  { k: 'You paste an address', t: 'It reads your page', d: 'the claim, the audience, the quotes, the price, the colours' },
  { k: 'One tap', t: 'You pick a look', d: 'three, drawn from your own site, each naming where it came from' },
  { k: 'Nothing to do', t: 'It writes the session', d: 'slides, the script beside each one, the polls, the demo cue' },
  { k: 'A time and a tap', t: 'It schedules your Zoom', d: 'meeting or webinar, the polls loaded in slide order, the join link back', now: true },
  { k: 'Nothing to do', t: 'The run measures itself', d: 'the recording arrives on its own, timed against the deck' },
];
const fmt = (s) => { s = Math.max(0, Math.round(s)); return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0'); };

function PresenterWindow() {
  const total = useRef(null), slide = useRef(null), over = useRef(null), rec = useRef(null);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    let t = 18 * 60 + 42, s = 2 * 60 + 47, o = 17;
    const id = setInterval(() => {
      t++; s++; o++;
      if (total.current) total.current.textContent = fmt(t);
      if (slide.current) slide.current.textContent = fmt(s);
      if (over.current) over.current.textContent = 'over by ' + fmt(o);
      if (rec.current) rec.current.textContent = fmt(t);
    }, 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <figure className="pw" aria-label="The presenter window, as it looks during a rehearsal">
      <div className="title"><span className="dots" aria-hidden="true"><i /><i /><i /></span>PRESENTER (yours): Your first hundred customers</div>
      <div className="clocks">
        <div className="clk"><div className="l">Total</div><div className="v" ref={total}>18:42</div><div className="s">of 33:30</div></div>
        <div className="clk over"><div className="l">Slide 7 of 13</div><div className="v" ref={slide}>2:47</div><div className="s" ref={over}>over by 0:17</div></div>
        <div className="clk"><div className="l">Demo</div><div className="v">1:56</div><div className="s">cue 4 of 9</div></div>
      </div>
      <div className="grid">
        <div>
          <div className="slide"><div className="k">Where the week actually goes</div><div className="b">SHARE THIS: your first hundred customers</div></div>
          <div className="next"><b>Next:</b> The one habit to start this week</div>
        </div>
        <div className="script">
          <div className="script-h">Script</div>
          <p className="cue done">Open the dashboard. Do not click anything yet.</p>
          <p className="cue lit">Scroll to the weekly view. This is the whole point of the demo.</p>
          <p className="cue">Pause here for the poll. Forty seconds, then move on whatever the split says.</p>
          <p>One habit decides it. Say it once, plainly, and let it sit.</p>
        </div>
      </div>
      <div className="foot"><span>Pace <span className="pace fast">171 wpm</span> (target 140 to 160)</span><span className="rec"><i aria-hidden="true" />REC <span ref={rec}>18:42</span></span></div>
      <figcaption className="visually-hidden">A presenter window showing the current slide, the script with the current cue lit, three clocks, and a pace meter reading 171 words a minute against a target of 140 to 160.</figcaption>
    </figure>
  );
}

const cta = (which, surface) => () => track(EVENTS.LANDING_CTA_CLICKED, { cta: which, surface });

/** The hero's one field. It carries the address to the builder, which reads the page as soon as the founder is
 * signed in; nothing is read here, and the address is the only thing that crosses. */
function PageDoor() {
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const go = (e) => {
    e.preventDefault();
    const v = url.replace(/\s+/g, '').trim();
    if (!v) return;
    track(EVENTS.LANDING_PAGE_SUBMITTED, { typed: true });
    navigate(`/app?page=${encodeURIComponent(v)}`);
  };
  return (
    <form className="page-door" onSubmit={go}>
      <label className="visually-hidden" htmlFor="hero-url">Your landing page address</label>
      <div className="field">
        <span className="scheme" aria-hidden="true">https://</span>
        <input id="hero-url" type="text" inputMode="url" autoComplete="url" placeholder="yoursite.com"
               value={url} maxLength={2048} onChange={(e) => setUrl(e.target.value)} />
      </div>
      <button type="submit" className="btn primary">Read my page</button>
    </form>
  );
}

export default function LandingPage() {
  usePageMeta({
    title: 'Fullrun: rehearse your weekly webinar before you go live',
    description: 'Fullrun turns your landing page into next week\'s webinar: the session written in your own words and colours, rehearsed against the clock, scheduled in your Zoom with the polls loaded, and the recording measured when it is over.',
    path: '/',
  });
  const onFaq = (q) => (e) => { if (e.currentTarget.open) track(EVENTS.FAQ_OPENED, { question: q }); };

  return (
    <>
      <header className="wrap">
        <nav aria-label="Primary">
          <Link className="logo" to="/">Fullrun</Link>
          <div className="links">
            <a href="#full-run">How it works</a>
            <a href="#questions">Questions</a>
            <Link className="btn quiet small" to="/app">Open the builder</Link>
          </div>
        </nav>
      </header>

      <main>
        <section className="hero">
          <div className="wrap two">
            <div>
              <h1>Your landing page becomes next week&apos;s webinar.</h1>
              <p className="sub">Fullrun reads your page, writes the session in your own words and colours, schedules it in your Zoom with the polls loaded, and measures the recording when it is over.</p>
              <PageDoor />
              <p className="door-note">No page yet? <Link to="/app" onClick={cta('brief', 'hero')}>Answer seven questions instead.</Link> Either way you sign in once, so the deck is yours and stays yours.</p>
              <p className="source">Free while it is new. Your recordings are yours: uploaded only when you choose, analyzed for you, readable by no one else.</p>
            </div>
            <PresenterWindow />
          </div>
        </section>

        {/* Round 13, A: what Fullrun is, end to end, in five steps, each saying what it costs the founder. The
            room is step four; before rooms the page described a rehearsal tool and stopped. */}
        <section className="arc-band">
          <div className="wrap">
            <ol className="arc">
              {ARC.map((a) => (
                <li className={`step${a.now ? ' now' : ''}`} key={a.t}>
                  <div className="k">{a.k}</div>
                  <div className="t">{a.t}</div>
                  <div className="d">{a.d}</div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <BuildFilm />

        {/* Story #24, variant A: two rows of sourced tiles, the problem then what moves the odds.
            The numbers come from data/evidence.js, pinned to the research record by evidence.test.js. */}
        <section className="alt" id="evidence">
          <div className="wrap">
            <h2>Anyone can build a product now. Launching one still means showing up.</h2>
            <p className="sub" style={{ marginTop: 10, fontSize: 19, maxWidth: '60ch' }}>The founders who get a product off the ground do it in front of people, on a weekly webinar. Fullrun is the hour before you go live, and now the hour itself.</p>
            <h3 className="h3" style={{ marginTop: 30 }}>{problem.heading}</h3>
            <p className="sub" style={{ marginTop: 8, fontSize: 17 }}>{problem.sub}</p>
            <div className="tiles">
              {problem.tiles.map((t) => <div className="tile" key={t.tag}><div className="n">{t.n}</div><p className="l">{t.line}</p><p className="s">{t.source}</p></div>)}
            </div>
            <EvidenceCharts />
            <h2 className="h3" style={{ marginTop: 34 }}>{odds.heading}</h2>
            <div className="tiles">
              {odds.tiles.map((t) => <div className="tile" key={t.tag}><div className="n">{t.n}</div><p className="l">{t.line}</p><p className="s">{t.source}</p></div>)}
            </div>
            <div className="tiles two">
              {odds.facts.map((t) => <div className="tile" key={t.tag}><div className="n">{t.n}</div><p className="l">{t.line}</p><p className="s">{t.source}</p></div>)}
            </div>
            <p className="sub" style={{ marginTop: 26, fontSize: 19 }}>{odds.turn}</p>
            <h2 className="h3" style={{ marginTop: 22 }}>{odds.toolsLead}</h2>
            <div className="tools">
              {odds.tools.map((t) => <div key={t.title}><h3>{t.title}</h3><p>{t.line}</p></div>)}
            </div>
          </div>
        </section>

        <section id="why">
          <div className="wrap">
            <h2>Why the launch is a weekly webinar</h2>
            <div className="three">
              <div><h3>Building got easy</h3><p>Anyone with a laptop and an idea can ship a product in a weekend now. What has not changed is the launch. A product launches when there are people who want it, tell other people, and show up when you ask them to. That is a community, and a landing page does not make one.</p></div>
              <div><h3>Community is built in the room</h3><p>You get it by being in front of your customers, on a schedule, teaching them something they can use. For a first-time founder the most effective place for that is a webinar, and the ones that work are weekly. Same day, same time, a room that grows.</p></div>
              <div><h3>So you present, every week</h3><p>How you present is most of what the room remembers. Not the slides. The pace, the pauses, whether the demo moved when you said it would, whether you rushed the part that mattered. That is what a full run is for.</p></div>
            </div>
          </div>
        </section>

        <section id="full-run">
          <div className="wrap two">
            <div>
              <h2>What a full run is</h2>
              <p>A full run is the whole session, start to finish, camera on, without stopping. Not a read-through. Not a look at the slides. The thing itself, as it will happen, then measured.</p>
              <p>Fullrun builds two windows from one file: the deck your audience sees, and a presenter window only you see, with the script beside each slide, a clock per slide that turns amber when you run over, and the demo's cues lit as the film plays. You record a full run. The review reads the recording: pace over time, fillers by word, pauses over three seconds, time per slide against its target. Then you change the deck from what the recording showed, not from how it felt.</p>
            </div>
            <div className="card">
              <p className="big">Three full runs in one day took fillers from 2.3 a minute to 0.5, and pace from 194 words a minute into the target band.</p>
              <p className="source">Source: the founder's own rehearsal log for a weekly webinar, 15 September 2026, measured by the review from the recordings, not estimated. Fullrun was built to run that webinar.</p>
            </div>
          </div>
        </section>

        {/* Round 13, A: rooms changed what the product is, so the page says so plainly. Every claim here is a
            capability the seam actually declares (ADR 0003); Meet is named without polls rather than with them. */}
        <section className="alt" id="rooms">
          <div className="wrap">
            <h2>It runs where you already run it.</h2>
            <p className="sub" style={{ marginTop: 10, maxWidth: '62ch' }}>Connect Zoom once. Fullrun schedules the session from the deck, loads the deck&apos;s polls in slide order, and when Zoom says the recording is ready it becomes a run, timed against the deck you scheduled it with. You never upload anything.</p>
            <div className="three" style={{ marginTop: 22 }}>
              <div>
                <h3>Zoom, meeting or webinar</h3>
                <p>A meeting works on the Zoom plan you already pay for: the polls, cloud recording and the attendance report. A webinar adds registration and the questions report, if your account has that licence.</p>
              </div>
              <div>
                <h3>Google Meet and Zoho Meeting, not yet</h3>
                <p>Both are named in the product with what they can do, and neither can be connected yet. Google Meet has no polls and no registration, so Fullrun will show it without them rather than with buttons that do nothing.</p>
              </div>
              <div>
                <h3>Or no room at all</h3>
                <p>Share the deck window in anything that shares a window, record it yourself, and upload it when you want it measured. The room saves you the trip; it was never the point.</p>
              </div>
            </div>
            <div className="card scheduled-card">
              <span className="pill ok">Scheduled in Zoom meeting, 4 polls loaded</span>
              <dl className="kv">
                <dt>When</dt><dd>2026-10-08 at 12:00 America/Los_Angeles, 34 minutes</dd>
                <dt>Join link</dt><dd>us06web.zoom.us/j/81234567890</dd>
              </dl>
              <p className="source">What the builder shows once a session exists. The date, the length and the polls are the deck&apos;s own.</p>
            </div>
          </div>
        </section>

        {/* Round-6 mocks, variant A (founder, 2026-09-17): five collapsed objections in the order visitors ask
            them, then a closing block that restates the value and repeats the primary action. */}
        <section id="questions">
          <div className="wrap narrow">
            <h2>Questions before you start</h2>
            <div className="faq">
              <details onToggle={onFaq('free')}><summary>Is it free?</summary><p>Yes, while it is new. When a paid plan exists, its price and what it includes will be on this page before you pay, and nothing you have built or recorded changes.</p></details>
              <details onToggle={onFaq('zoom')}><summary>Do I need Zoom?</summary><p>No. The deck window is a browser tab; share it in Zoom, Meet or anything that shares a window, and only that window, and the rehearsal studio records in the browser with no Zoom at all. If you do use Zoom, connect it once: Fullrun schedules the session from the deck, loads the polls, and brings the recording back measured.</p></details>
              <details onToggle={onFaq('existing_slides')}><summary>Does it work with my existing slides?</summary><p>The deck is one markdown file with the script beside each slide, so a slide deck comes across as text: paste the words, keep the demo as a film. PowerPoint and Keynote files are not imported as they are.</p></details>
              <details onToggle={onFaq('recordings')}><summary>What happens to my recordings?</summary><p>Uploaded only when you click to upload, stored under your account, readable by you and no one else, deleted when you delete them. Every number on a review names what it was measured from. The deck builder itself uploads nothing.</p></details>
              <details onToggle={onFaq('how_long')}><summary>How long does a run take?</summary><p>As long as your webinar, plus a few minutes for the review to come back. The first run is the slowest part; from the second, the table shows whether this week is better than last.</p></details>
            </div>
          </div>
        </section>

        <section className="alt" id="start">
          <div className="wrap narrow">
            <div className="close">
              <h2>Rehearse the next one before you go live.</h2>
              <p>One file becomes both windows. One run becomes three fixes and a revised script. The room gets the version you practised.</p>
              <div className="ctas">
                <Link className="btn primary" to="/app" onClick={cta('builder', 'end')}>Start from your landing page</Link>
                <a className="btn quiet" href="#full-run" onClick={cta('full_run', 'end')}>See how a run is measured</a>
              </div>
              <p className="source">Free while it is new. Your recordings are yours.</p>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </>
  );
}
